import { yupResolver } from '@hookform/resolvers/yup';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Grid,
  Box,
  Button,
  Checkbox,
  Paper,
  Stack,
  FormHelperText,
  FormControlLabel,
  TextField,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  ListItemText,
  Autocomplete,
  Tooltip,
  Typography,
  styled,
  tooltipClasses,
  TooltipProps,
  Divider,
  CircularProgress,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  generateJobCode,
  getJobByCompanyNameAndClientJobCode,
  getSkillsSuggestions,
  getTagsSuggetions,
  IJob,
  IJobFieldSettings,
  IJobInsight,
  indexJob,
  IUserDTO,
  JobType,
  amplitude,
  ClientDTO,
  ClientContactDTO,
  mapJobWithEvaluation,
  ILocation,
  fetchQuestionsForJob,
  fetchQuestionsForJobDescription,
  csrfTokenCall,
  mapJobWithInformationTemplate,
  fetchInfoTemplateByJobId,
  fetchEvalTemplateByJobId,
  accountManagerAutoComplete,
} from 'wisestep-api';
import {
  FormWizard,
  ControlledTextInput,
  WsLocationSearch,
  WsButtonGroup,
  WsSelect,
  WsAutoComplete,
  WsInputNumber,
  Label,
  WsEditor,
  useWsEditor,
  WsSelectObj,
  WsToggleButton,
  WsButton,
  WsTagsList,
  WsDialog,
  WsDialogSize,
  ChipsType,
  WsTypography,
  WsTypoVariants,
  WsIconMoreVer,
  WsElementWithPopOver,
  WsTypoColors,
} from 'wisestep-base';

import { ClientAutoCompleteFilter, ClientContactAutoComplete } from '../../../../components';
import { FeeArrangementSelect } from '../../../../components/FeeArrangementSelect';
import JobWorkplacePreferenceFilter from '../../../../components/filters/components/JobWorkplacePreferenceFilter';
import SubmissionFormTemplateAutoComplete from '../../../../components/filters/components/SubmissionFormTemplateAutoComplete';
import WsCheckBox from '../../../../components/WsCheckBox/WsCheckBox';
import { useUserContext, useWorkspaceContext } from '../../../../context';
import {
  rtrAmplitudeEvents,
  evaluationAmplitudeEvents,
  informationReqAmplitudeEvents,
} from '../../../../features/conversationWidget/utils/rtrHelper';
import ArtificialIntelligence from '../../../../images/ArtificialIntelligence.svg';
import {
  getCurrencies,
  getSalaryTimeSpans,
  getDefaultTimeSpan,
} from '../../../../utils/CurrencyHelper';
import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import {
  extractEmail,
  extractNameOrEmail,
  getUserNameAndEmail,
} from '../../../../utils/StringUtils';
import { assignJobToCreator } from '../../helper';
import {
  getCategories,
  transformIJobTagsToStringArray,
  transformISkillsToStringArray,
  jobValidation,
  IJobOccupation,
  IJobCategory,
  getJobIndustries,
} from '../../helper/JobHelperService';

import CreateFeeArrangementDialog from './CreateFeeArrangementDialog';
import CreateNewClientContactDialog from './CreateNewClientContactDialog';
import CreateNewClientDialog from './CreateNewClientDialog';
import SelectEvaluationDrawer, { IEValuationTemplateDetails } from './SelectEvaluationDrawer';
import SelectInformationTemplateDrawer, {
  IInformationTemplateDetails,
} from './SelectInformationTemplateDrawer';

export interface IJobPostingForm {
  jobEntity?: IJob;
  jobFieldSettings: IJobFieldSettings;
  onNavigateBack: (value: string) => void;
  businessUnits?: WsSelectObj[];
}
const payRateExpensesTypes = [
  {
    label: 'Percentage',
    value: 'PERCENTAGE',
  },
  {
    label: 'Fixed',
    value: 'FIXED',
  },
];

const JobPostingForm = ({
  jobEntity,
  jobFieldSettings,
  onNavigateBack,
  businessUnits,
}: IJobPostingForm) => {
  const [jobData] = useState<IJob>({
    ...jobEntity,
    companyName: jobEntity?.companyName ?? '',
    salaryCurrency:
      jobEntity?.salaryCurrency ?? jobFieldSettings.defaultCurrency?.selectedField ?? '',
    salaryTimeSpan:
      jobEntity?.salaryTimeSpan ??
      getDefaultTimeSpan(jobFieldSettings.salaryTimeSpan?.selectedField),
    payRateExpensesType: jobEntity?.payRateExpensesType ?? 'FIXED',
    clientBillRateCurrency:
      jobEntity?.clientBillRateCurrency ??
      jobEntity?.salaryCurrency ??
      jobFieldSettings.defaultCurrency?.selectedField ??
      '',
    clientBillRateTimeSpan:
      jobEntity?.clientBillRateTimeSpan ??
      getDefaultTimeSpan(jobFieldSettings.salaryTimeSpan?.selectedField),
  });
  const [jobDataNew] = useState<IJob>({
    ...jobEntity,
    companyName: jobEntity?.companyName ?? '',
    salaryCurrency:
      jobEntity?.salaryCurrency ?? jobFieldSettings.defaultCurrency?.selectedField ?? '',
    salaryTimeSpan:
      jobEntity?.salaryTimeSpan ??
      getDefaultTimeSpan(jobFieldSettings.salaryTimeSpan?.selectedField),
    payRateExpensesType: jobEntity?.payRateExpensesType ?? 'FIXED',
    clientBillRateCurrency:
      jobEntity?.clientBillRateCurrency ??
      jobEntity?.salaryCurrency ??
      jobFieldSettings.defaultCurrency?.selectedField ??
      '',
    clientBillRateTimeSpan:
      jobEntity?.clientBillRateTimeSpan ??
      getDefaultTimeSpan(jobFieldSettings.salaryTimeSpan?.selectedField),
  });
  const navigate = useNavigate();
  const validationsSchema = jobValidation(jobFieldSettings);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const workspace = useWorkspaceContext();
  const methods = useForm<IJob>({
    resolver: yupResolver(validationsSchema),
    defaultValues: jobData,
  });

  const {
    setValue,
    watch,
    handleSubmit,
    getValues,
    formState: { errors },
    getFieldState,
  } = methods;

  const [disableSubmitButton, setDisableSubmitButton] = useState<boolean>(false);

  const setNotification = useNotificationStore((state) => state.setNotification);
  const [formWizardCurrentStep, setFormWizardCurrentStep] = useState(0);
  const [skillList, setSkillList] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [accountManagers, setAccountManagers] = useState<string[]>([]);
  const [openNewClientDialogue, setOpenNewClientDialogue] = useState<boolean>(false);
  const [isMarkInactive, setIsMarkInactive] = useState<boolean>(false);
  const [openClientContactDialog, setOpenClientContactDialog] = useState<boolean>(false);
  const [openFeeArrangementDialog, setOpenFeeArrangementDialog] = useState<boolean>(false);
  const [isFeeArrangementAdded, setIsFeeArrangementAdded] = useState<boolean>(false);

  const [openEvaluationDrawer, setOpenEvaluationDrawer] = useState<boolean>(false);
  const [openInformationDrawer, setOpenInformationDrawer] = useState<boolean>(false);
  const [selectedEvaluations, setSelectedEvaluations] = useState<IEValuationTemplateDetails[]>([]);
  const [selectedInfoTemplates, setSelectedInfoTemplates] = useState<IInformationTemplateDetails[]>(
    []
  );
  const [hasRtrTemplates, setHasRtrTemplates] = useState<boolean>(false);
  const [companyNameNewVal, setCompanyNameNewVal] = useState<string>('');
  const [companyContactInput, setCompanyContactInput] = useState<string>('');
  const [jobOccupations, setJobOccupations] = useState<IJobOccupation[]>([]);
  const [isCareerPage, setIsCareerPage] = useState<boolean>(jobEntity?.addToCareerPage ?? true);
  const [token, setToken] = useState<string>('');
  const [auth, setAuth] = useState<string>('');
  const [openQuestionsDialog, setOpenQuestionsDialog] = useState(false);
  const [openQuestionEditDialog, setOpenQuestionEditDialog] = useState(false);
  const [openAddQuestionDialog, setOpenAddQuestionDialog] = useState(false);
  const [isQuestionGeneretedAndSaved, setIsQuestionGeneretedAndSaved] = useState(false);
  const [warningStatus, setWarningStatus] = useState(false);
  const [textFieldValue, setTextFieldValue] = useState('');
  const [textFieldValueOnAdd, setTextFieldValueOnAdd] = useState('');
  const [selectedText, setSelectedText] = useState('');

  const [jobQuestions, setJobQuestions] = useState<string[]>([]);
  const [jobQuestionsTemp, setJobQuestionsTemp] = useState<string[]>([]);
  const abortControllerRef = useRef(new AbortController());
  const defaultInsight =
    jobData?.insights && !isEmpty(jobData?.insights[0]) ? jobData.insights[0].insight : '';

  const onJobDescUpdate = (args: string | null) => {
    setValue('description', args ? args : '');
  };
  const jdEditor = useWsEditor(jobData?.description || '', onJobDescUpdate);

  const jobCategory = watch('jobCategory');
  const companyName = watch('companyName') ?? '';
  const onJobInsightsUpdate = (args: string | null) => {
    const insights: IJobInsight[] | undefined = jobData?.insights;
    if (insights) {
      const insightsMatched: IJobInsight[] = insights.filter((ins) => ins.id === '');
      if (insightsMatched && insightsMatched?.length > 0) {
        insightsMatched[0].insight = args ?? '';
      } else {
        insights.push({
          id: '',
          jobId: '',
          insight: args ?? '',
          createdBy: '',
          createdAt: new Date(),
        });
      }
      setValue('insights', insights);
    } else {
      setValue('insights', [
        { id: '', jobId: '', insight: args ?? '', createdBy: '', createdAt: new Date() },
      ]);
    }
  };

  useEffect(() => {
    const getToken = async () => {
      const res = await csrfTokenCall();
      const token = res.config.headers?.['X-XSRF-TOKEN'];
      const auth = res.config.headers?.['Authorization'];
      setToken(token ?? '');
      setAuth(auth ?? '');
      return token;
    };
    getToken();
  }, []);

  const enteredRecruiter = watch('clientRecruiter') ?? '';
  const data = enteredRecruiter.includes('|') ? enteredRecruiter.split('|')[0] : enteredRecruiter;
  const clientRecruiter = data?.split(' - ')[1] ?? data;
  const useRedactedCv = watch('useRedactedCv');
  useEffect(() => {
    if (isEmpty(companyName?.trim())) setValue('clientRecruiter', '');
  }, [enteredRecruiter, companyName, setValue]);

  const onAccountManagerInputKey = useCallback(async (searchText) => {
    if (searchText?.length > 2) {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const newAbortController = new AbortController();
      abortControllerRef.current = newAbortController;
      try {
        const response = await accountManagerAutoComplete(
          `${encodeURIComponent(
            searchText
              .trim()
              .split(' ')
              .map((name) => (name === '' ? '' : `${name}:*`))
              .join(' & ')
          )}`,
          newAbortController.signal
        );
        setAccountManagers(response.data?.map((user) => getUserNameAndEmail(user)) ?? []);
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    }
  }, []);

  const trimQuestion = (questions: string[]) =>
    questions.map((question) => question.replace(/^\d+\.\s*/, ''));

  useEffect(() => {
    if (jobEntity?.id) {
      const getEvaluationAndInformationMappings = async () => {
        const evalResponse = await fetchEvalTemplateByJobId(jobEntity.id as string);
        if (evalResponse) {
          setSelectedEvaluations(evalResponse.data);
        }
        const infoResponse = await fetchInfoTemplateByJobId(jobEntity.id as string);
        if (infoResponse) {
          setSelectedInfoTemplates(infoResponse.data);
        }
      };
      getEvaluationAndInformationMappings();
    }
  }, [jobEntity]);
  const generateInterviewQuestions = async () => {
    try {
      const description = getValues('description');
      if (jobEntity?.id == null && description) {
        const response = await fetchQuestionsForJobDescription(description);
        const questions = response.data;
        const trimmedQuestions = trimQuestion(questions);
        if (trimmedQuestions && trimmedQuestions.length > 0) {
          setJobQuestions(trimmedQuestions);
          setJobQuestionsTemp(trimmedQuestions);
          setValue('questions', trimmedQuestions ?? []);
          setOpenQuestionsDialog(true);
        } else {
          setNotification(warning('Not able to generate questions, Please Try again!'));
        }
      } else {
        if (jobEntity?.questions && jobEntity?.questions?.length > 0) {
          const trimmedQuestions = trimQuestion(jobEntity?.questions);
          setJobQuestions(trimmedQuestions);
          setOpenQuestionsDialog(true);
        } else if (jobEntity && jobEntity.id) {
          const response = await fetchQuestionsForJob(jobEntity.id);
          const questions = response.data;
          const trimmedQuestions = trimQuestion(questions);
          if (trimmedQuestions && trimmedQuestions.length > 0) {
            setJobQuestions(trimmedQuestions);
            setJobQuestionsTemp(trimmedQuestions);
            setValue('questions', trimmedQuestions ?? []);
            setOpenQuestionsDialog(true);
          } else {
            setNotification(warning('Not able to generate questions, Please Try again!'));
          }
        }
      }
    } catch (error) {
      console.error('Error in generateInterviewQuestions:', error);
    }
  };

  const jobInsightEditor = useWsEditor(defaultInsight, onJobInsightsUpdate);
  const jobCategories: IJobCategory[] = getCategories();
  const selectedCategory = useMemo(() => {
    const selected: IJobCategory[] = jobFieldSettings.jobCategory.selectedFields?.map((category) =>
      JSON.parse(category)
    );
    return isEmpty(selected) ? jobCategories : selected;
  }, [jobCategories, jobFieldSettings.jobCategory.selectedFields]);

  useEffect(() => {
    const setJobCode = async () => {
      const jobCode = await generateJobCode();
      setValue('jobCode', jobCode.data.code);
    };

    if (!jobEntity?.id) {
      setJobCode();
      setValue('numberOfPositions', 1);
    }
  }, [jobEntity, setValue]);

  useEffect(() => {
    selectedCategory.map((object) => {
      try {
        if (object.value === jobCategory) {
          setJobOccupations(object.occupations);
        }
      } catch (e) {
        if (object.label == jobCategory) {
          setJobOccupations((prevOccupations) => {
            const newOccupations = object.occupations;
            newOccupations.map((occupation) => {
              if (prevOccupations.includes(occupation)) {
                setValue('jobOccupation', JSON.stringify(occupation));
              }
            });
            setValue('jobCategory', JSON.stringify(object));
            return newOccupations;
          });
        }
      }
    });
  }, [jobCategory, selectedCategory, setValue]);

  const currencies = getCurrencies().map((val) => {
    return { label: val, value: val };
  });

  const payRateExpensesType = methods.watch('payRateExpensesType');
  const selectedType = watch('jobType') ?? '';
  const allowedTypes = [
    'Contract',
    'Temporary',
    'Internship',
    'Freelance',
    'ContractToHireW2',
    'ContractW2',
    'ContractIndependent',
    'ContractToHireIndependent',
    'ContractCorpToCorp',
    'ContractToHireCorpToCorp',
    'PartTime',
    'FixedTermContract',
  ];
  const isValidContractType = allowedTypes.includes(selectedType);

  const jobTypes = jobFieldSettings.jobType?.selectedFields;
  const workDurationTimpSpans: WsSelectObj[] = [
    { label: 'Weeks', value: 'Weeks' },
    { label: 'Months', value: 'Months' },
    { label: 'Years', value: 'Years' },
  ];
  const taxTermsArray = ['C2C', 'W2', '1099'];
  const jobStatusList = jobFieldSettings.jobStatusType?.availableFields;
  const hiringForList: WsSelectObj[] = jobFieldSettings.hiringFor?.availableFields.map(
    (dropDownItem: string) => ({
      label: dropDownItem,
      value: dropDownItem,
    })
  );

  const salaryTimeSpans = getSalaryTimeSpans();
  const jobIndustries = getJobIndustries();
  const jobIndustry: WsSelectObj[] = jobFieldSettings.jobIndustryName?.availableFields.map(
    (dropDownItem: string) => ({
      label: dropDownItem,
      value: dropDownItem,
    })
  );
  const jobIndustryList = isEmpty(jobIndustry) ? jobIndustries : jobIndustry;

  const taxTermDefaultValueIndex = jobEntity?.taxTerms
    ? taxTermsArray.indexOf(jobEntity?.taxTerms)
    : -1;

  const jobStatusDefaultValueIndex = () => {
    let index = jobStatusList?.indexOf('FullTime') ?? -1;
    if (jobEntity?.jobStatusType) index = jobStatusList?.indexOf(jobEntity?.jobStatusType) ?? -1;
    index > -1 && onJobStatusChange(index, jobStatusList[index]);
    return index;
  };

  const onJobTypeChange = (index: number, value: string) => {
    setValue('jobType', value as unknown as JobType);
  };

  const onTaxTermsChange = (index: number, value: string) => {
    setValue('taxTerms', value);
  };
  const onJobStatusChange = (index: number, value: string) => {
    setValue('jobStatusType', value);
  };

  const onSubmit = async (jobEntity: IJob) => {
    amplitude('job_manual', {
      clickedOn: 'Post Manually',
      pageName: 'new jobs',
      pageUrl: window.location.href,
      workspaceName: workspace.name,
      planType: workspace.planType,
      interfaceType: 'Web',
    });
    try {
      setDisableSubmitButton(true);
      const buId =
        getValues('businessUnit')?.trim() === 'notAvailable'
          ? null
          : getValues('businessUnit')?.trim();
      jobEntity.remoteJob = jobEntity.workplacePreference?.toLowerCase() == 'remote';
      jobEntity.companyName = companyName?.trim();
      if (companyName.includes('->')) {
        jobEntity.companyName = companyName?.split('->')[1]?.trim();
      }
      jobEntity.clientRecruiter = clientRecruiter?.trim();
      jobEntity.clientRecruiterName = getValues('clientRecruiterName')?.trim();
      jobEntity.businessUnit = buId;
      jobEntity.businessUnitName = buId === null ? null : getBuName(buId);
      jobEntity.addToCareerPage = isCareerPage;
      jobEntity.questions = jobQuestions;
      if (!isValidContractType) {
        jobEntity.clientBillRateCurrency = undefined;
        jobEntity.clientBillRateTimeSpan = undefined;
        jobEntity.clientBillRate = 0;
      }
      if (!jobEntity.payRateExpenses) {
        jobEntity.payRateExpensesCurrency = undefined;
        jobEntity.payRateExpensesType = undefined;
      }

      const updatedArray = jobEntity?.locations?.map((item) => {
        if (item.keywords) {
          const newLocation: ILocation = {
            area: item.area,
            city: item.city,
            state: item.state,
            stateCode: item.stateCode,
            country: item.country,
            countryCode: item.countryCode,
            continent: item.continent,
            continentCode: item.continentCode,
            zipCode: item.zipCode,
            lat: item.lat,
            lon: item.lon,
            point: item.lat + ',' + item.lon,
          };
          return newLocation;
        } else {
          return item;
        }
      });
      jobEntity.locations = updatedArray;
      jobEntity.useRedactedCv = useRedactedCv ?? false;
      const indexResultResponse = await indexJob(jobEntity);
      const indexResult: IJob = indexResultResponse.data;
      if (indexResult.id) {
        amplitude('Job_Details_success', {
          clickedOn: 'Post job and success',
          pageName: 'new jobs',
          pageUrl: window.location.href,
          workspaceName: workspace.name,
          planType: workspace.planType,
          interfaceType: 'Web',
        });
        setNotification(success('Job information saved successfully.'));
        isEmpty(jobEntity.id) &&
          setNotification(await assignJobToCreator(indexResult.id, loggedInUser?.email ?? ''));
        try {
          createJobEvaluationsMappings(indexResult.id);
          createJobInfoTemplateMappings(indexResult.id);
        } catch (err: any) {
          setNotification(warning('Error while creating Evaluation/Information template mappings'));
        }
        navigate(`/jobs/${indexResult.id}/assign`);
      }
    } catch (error: any) {
      setDisableSubmitButton(false);
      if (error.response.status === 400)
        setNotification(
          warning(
            `A job is already posted with client job code ${jobEntity.clientJobCode} for the company ${jobEntity.companyName}`
          )
        );
      else setNotification(failed('Failed to save Job information'));
    }
  };

  const createJobEvaluationsMappings = async (jobId: string) => {
    const templates = selectedEvaluations.map((item) => parseInt(item.id));
    await mapJobWithEvaluation({
      jobId: jobId ?? '',
      templates,
    });
    if (templates.length > 0) {
      evaluationAmplitudeEvents(
        'eval_template_job',
        'Tagging an evalution template to a job',
        workspace
      );
    }
  };

  const createJobInfoTemplateMappings = async (jobId: string) => {
    await mapJobWithInformationTemplate({
      jobId: jobId ?? '',
      templates: selectedInfoTemplates.map((item) => parseInt(item.id)),
    });
    if (hasRtrTemplates) {
      rtrAmplitudeEvents('rtr_job', 'RTR tagged to job', workspace);
    }
    informationReqAmplitudeEvents(
      'info_template_job',
      'Information Request tagged to job',
      workspace
    );
  };

  const onSkillInputKey = async (skillInput: string) => {
    const result = await getSkillsSuggestions(skillInput);
    if (result.data) {
      setSkillList(transformISkillsToStringArray(result.data));
    }
  };

  const onTagInputKey = async (tagInput: string) => {
    const result: any = getTagsSuggetions(tagInput);
    if (result?.data) {
      setTags(transformIJobTagsToStringArray(result.data));
    }
  };

  const onJobTagSelection = (selectedTags: string[]) => {
    setValue('tags', selectedTags ?? []);
  };

  const onSkillSelection = (skillTags: string[]) => {
    setValue('skills', skillTags ?? []);
  };

  const onClientCreateFromDialogue = (selectedClient: ClientDTO) => {
    setValue('companyName', selectedClient?.name);
    setValue('clientId', selectedClient.id);
    setOpenNewClientDialogue(false);
  };

  const onClientContactCreateFromDialog = (selectedClientRecruiter: ClientContactDTO) => {
    const fullName = selectedClientRecruiter?.firstName
      .concat(' ' + selectedClientRecruiter?.lastName)
      .trim();

    setValue('clientRecruiter', `${fullName} - ${selectedClientRecruiter.emailAddress}`);
    setValue('clientContactId', selectedClientRecruiter?.id ?? '');
    setValue('clientRecruiterName', fullName);
    setOpenClientContactDialog(false);
  };

  const onAccountManagerSelect = (accountManager: string[]) => {
    if (accountManager) {
      setValue('accountManagerEmail', extractEmail(accountManager?.[0]));
      setValue('accountManagerName', extractNameOrEmail(accountManager?.[0]));
    }
  };
  const onError = () => {
    amplitude('Job_Details_failed', {
      clickedOn: 'Post job and missing details',
      pageName: 'new jobs',
      pageUrl: window.location.href,
      workspaceName: workspace.name,
      planType: workspace.planType,
      interfaceType: 'Web',
    });
    setNotification(warning('Please enter required fields'));
  };

  const onEditQuestion = (question: string) => {
    if (openAddQuestionDialog) setWarningStatus(true);
    else {
      setTextFieldValue(question);
      setSelectedText(question);
      setOpenQuestionEditDialog(true);
    }
  };

  const editQuestion = () =>
    setJobQuestions(
      jobQuestions.map((ques) => {
        if (ques === selectedText) return textFieldValue;
        return ques;
      })
    );

  const addQuestion = (question: string) => {
    if (!jobQuestions.includes(question) && question) setJobQuestions([...jobQuestions, question]);
    setTextFieldValueOnAdd('');
  };

  const onDeleteQuestion = (question: string) =>
    setJobQuestions(jobQuestions.filter((ques) => ques !== question));

  const handleInputChange = (event) => setTextFieldValue(event.target.value);

  const handleInputChangeOnAdd = (event) => setTextFieldValueOnAdd(event.target.value);

  interface CustomTooltipProps extends TooltipProps {
    className?: string;
  }
  const CustomTooltip = styled(({ className, ...props }: CustomTooltipProps) => (
    <Tooltip {...props} classes={{ popper: className ? className : undefined }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#484D6D',
      color: 'white',
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: '#484D6D',
    },
  });

  const onContinue = (questions: string[]) => {
    setJobQuestions(questions);
    setJobQuestionsTemp(questions);
  };

  const onCancel = (questions: string[]) => setJobQuestions(questions);

  const onClientJobCodeChange = async (clientJobCode: string) => {
    try {
      setValue('clientJobCode', clientJobCode);
      const result = await getJobByCompanyNameAndClientJobCode(companyName, clientJobCode);
      if (result?.data) {
        const jobByClientJobCode: IJob = result.data;
        if (jobData && jobByClientJobCode.id !== jobData.id) {
          setValue('clientJobCode', '');
          setNotification(failed('This Client Job Code is already used for another job.'));
          return;
        }
      }
    } catch (e) {
      if (!getValues('clientJobCode')) {
        setValue('clientJobCode', clientJobCode);
      }
    }
  };

  const onFormWizardArrowClick = () => onNavigateBack('choice');

  const formWizardMenuItemClick = (id: number) => {
    setFormWizardCurrentStep(id);
  };

  const menuItems = [
    {
      id: 0,
      label: 'Complete Job details',
      onClick: () => formWizardMenuItemClick(0),
    },
    {
      id: 1,
      label: 'Assign Team',
      onClick: () => formWizardMenuItemClick(1),
    },
    {
      id: 2,
      label: 'Publish Job',
      onClick: () => formWizardMenuItemClick(2),
    },
  ];
  let isUSA = false;

  methods.watch('locations')?.forEach((location) => {
    if (location.country == 'United States') isUSA = true;
  });

  const handleCheckbox = (e) => (e.target.checked ? setIsCareerPage(true) : setIsCareerPage(false));

  const onEvaluationTagDelete = (templateName: string) => {
    setSelectedEvaluations(
      [...selectedEvaluations].filter((obj) => obj.templateName !== templateName)
    );
  };

  const getQuestionButtonText = () => {
    const text = (jobEntity?.questions?.length ?? 0) > 0 ? 'View' : 'Generate';
    return text + ' Interview Questions';
  };

  const onInfoTagDelete = (templateName: string) => {
    setSelectedInfoTemplates(
      [...selectedInfoTemplates].filter((obj) => obj.categoryName !== templateName)
    );
  };

  const getBuName = (selectedId) => {
    const selectedBusinessUnit = businessUnits && businessUnits.find((b) => b.value === selectedId);
    return selectedBusinessUnit ? selectedBusinessUnit.label : '';
  };

  const onSaveQuestionResponse = () => {
    setIsQuestionGeneretedAndSaved(true);
    setOpenQuestionsDialog(false);
    onContinue(jobQuestions);
    amplitude('Save_Recruiter_Questions', {
      clickedOn: 'Save Button',
      pageName: 'new jobs',
      pageUrl: window.location.href,
    });
  };

  const onGenerateQuestions = () => {
    generateInterviewQuestions();
    amplitude('Generate_Recruiter_Questions', {
      clickedOn: 'Generate Button',
      pageName: 'new jobs',
      pageUrl: window.location.href,
    });
  };

  return (
    <>
      <FormWizard
        menuItems={menuItems}
        onArrowButtonClick={onFormWizardArrowClick}
        currentStep={formWizardCurrentStep}
        title="Post a new Job"
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit, onError)}>
            <Box sx={{ p: 4, borderWidth: '20px', borderColor: 'blue' }}>
              <Paper variant="outlined" square>
                <Stack direction="row" spacing={0}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        marginLeft: 4,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box>
                        <Grid container spacing={4} sx={{ mt: 0 }}>
                          {jobFieldSettings.title?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <ControlledTextInput
                                  name="title"
                                  labelText="Job Title"
                                  type="text"
                                  fullWidth
                                  id="job_title"
                                  placeholder="Eg, Java Developer"
                                  variant="outlined"
                                  autoComplete="title"
                                  showMandatorySymbol={jobFieldSettings.title?.mandatory}
                                  size="small"
                                />
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.locations?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <WsLocationSearch
                                  size="small"
                                  placeHolder="Job Location"
                                  name="locations"
                                  variant="outlined"
                                  showMandatorySymbol={jobFieldSettings.locations?.mandatory}
                                  wisestepApi={true}
                                  token={token}
                                  authorization={auth}
                                />
                              </Grid>
                            </>
                          )}
                          {!jobFieldSettings.workplacePreference?.hide && (
                            <>
                              <Grid item sm={5.75}>
                                <JobWorkplacePreferenceFilter
                                  name="workplacePreference"
                                  label="Workplace Preference"
                                  showMandatorySymbol={
                                    jobFieldSettings.workplacePreference?.mandatory
                                  }
                                />
                                <FormHelperText error>
                                  {errors.workplacePreference?.message}
                                </FormHelperText>
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.jobCode?.hide === false && (
                            <>
                              <Grid item sm={2.85}>
                                <ControlledTextInput
                                  name="jobCode"
                                  labelText="Job Code"
                                  type="text"
                                  fullWidth
                                  id="outlined-basic"
                                  placeholder="Eg, HGJCF4T"
                                  variant="outlined"
                                  disabled={true}
                                  autoComplete="jobCode"
                                  showMandatorySymbol={jobFieldSettings.jobCode?.mandatory}
                                  size="small"
                                />
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.numberOfPositions?.hide === false && (
                            <>
                              <Grid item sm={2.85}>
                                <WsInputNumber
                                  name="numberOfPositions"
                                  label="Positions"
                                  variant="outlined"
                                  minValue={1}
                                  maxValue={1000}
                                  showMandatorySymbol={
                                    jobFieldSettings.numberOfPositions?.mandatory
                                  }
                                />
                              </Grid>
                            </>
                          )}
                          <Grid item container spacing={4}>
                            {jobFieldSettings.jobType?.hide === false && (
                              <>
                                <Grid item sm={4} id="job_type">
                                  <WsButtonGroup
                                    inputTitle="Job Type"
                                    buttonsList={jobTypes}
                                    selectedIndexValue={onJobTypeChange}
                                    showMandatorySymbol={jobFieldSettings.jobType?.mandatory}
                                    defaultValue={
                                      jobEntity?.jobType
                                        ? jobTypes?.indexOf(jobEntity?.jobType) ?? -1
                                        : -1
                                    }
                                    size="large"
                                  ></WsButtonGroup>
                                  <FormHelperText error>{errors.jobType?.message}</FormHelperText>
                                </Grid>
                              </>
                            )}
                            {jobFieldSettings.taxTerms?.hide === false &&
                            isUSA &&
                            isValidContractType ? (
                              <Grid item sm={3.5}>
                                <WsButtonGroup
                                  inputTitle="Tax Terms"
                                  buttonsList={taxTermsArray}
                                  selectedIndexValue={onTaxTermsChange}
                                  showMandatorySymbol={jobFieldSettings.taxTerms?.mandatory}
                                  defaultValue={taxTermDefaultValueIndex}
                                  size="large"
                                />
                                <FormHelperText error>{errors.taxTerms?.message}</FormHelperText>
                              </Grid>
                            ) : (
                              <Grid item sm={1.75}></Grid>
                            )}
                            {jobFieldSettings.businessUnit?.hide === false && (
                              <>
                                <Grid item sm={4}>
                                  <WsSelect
                                    name="businessUnit"
                                    label="Business Unit"
                                    selectArray={businessUnits ?? []}
                                    disabled={businessUnits?.length === -1}
                                    variant="outlined"
                                    showMandatorySymbol={jobFieldSettings.businessUnit?.mandatory}
                                  />
                                </Grid>
                              </>
                            )}
                          </Grid>

                          {jobFieldSettings.companyName?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <ClientAutoCompleteFilter
                                  key={companyName}
                                  name="companyName"
                                  clientIdFieldName="clientId"
                                  showMandatorySymbol={jobFieldSettings.companyName?.mandatory}
                                  textForAddingNew="Client"
                                  onNewValueCreate={(data: string) => {
                                    setValue('companyName', ' ');
                                    setCompanyNameNewVal(data ?? '');
                                    setOpenNewClientDialogue(true);
                                  }}
                                  onStatusUpdateValue={(data: string) => {
                                    setValue('companyName', ' ');
                                    setCompanyNameNewVal(data ?? '');
                                    setIsMarkInactive(true);
                                    setOpenNewClientDialogue(true);
                                  }}
                                />
                              </Grid>
                            </>
                          )}

                          {jobFieldSettings.clientRecruiterName?.hide === false && (
                            <Grid item sm={5.75}>
                              <ClientContactAutoComplete
                                key={clientRecruiter}
                                name="clientRecruiter"
                                clientId={methods.getValues('clientId') ?? jobData.clientId ?? ''}
                                showMandatorySymbol={
                                  jobFieldSettings.clientRecruiterName?.mandatory
                                }
                                disabled={isEmpty(companyName?.trim())}
                                textForAddingNew="Client Contact"
                                clientName={companyName}
                                clientContactIdFieldName="clientContactId"
                                onNewValueCreate={(data: string) => {
                                  setValue('clientRecruiter', '');
                                  setOpenClientContactDialog(true);
                                  setCompanyContactInput(data);
                                }}
                                component="job"
                              />
                            </Grid>
                          )}
                          {jobFieldSettings.clientJobCode?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <ControlledTextInput
                                  name="clientJobCode"
                                  labelText="Client Job Code"
                                  type="text"
                                  fullWidth
                                  id="outlined-basic"
                                  placeholder="Eg, INFY23X"
                                  variant="outlined"
                                  size="small"
                                  onChange={(event) => onClientJobCodeChange(event.target.value)}
                                  showMandatorySymbol={jobFieldSettings.clientJobCode?.mandatory}
                                />
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.accountManagerName?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <WsAutoComplete
                                  onValueEnter={onAccountManagerInputKey}
                                  onTagsChange={onAccountManagerSelect}
                                  placeholder="Eg, Michael"
                                  initialValue={
                                    jobData?.accountManagerName ? [jobData?.accountManagerName] : []
                                  }
                                  optionsInStringArray={accountManagers}
                                  multiple={false}
                                  freeSolo={false}
                                  name="accountManagerName"
                                  label="Account Manager"
                                  size="small"
                                  showMandatorySymbol={
                                    jobFieldSettings.accountManagerName?.mandatory
                                  }
                                  variant="outlined"
                                />
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.feeArrangement?.hide === false && (
                            <Grid item sm={5.75}>
                              <FeeArrangementSelect
                                name="feeArrangementId"
                                key="feeArrangementId"
                                label="Fee Arrangement"
                                clientId={methods.getValues('clientId')}
                                companyName={companyName}
                                variant="outlined"
                                isFeeArrangementAdded={isFeeArrangementAdded}
                                openDialogCallBack={() => setOpenFeeArrangementDialog(true)}
                                showMandatorySymbol={jobFieldSettings.feeArrangement?.mandatory}
                                jobType={isValidContractType ? 'CONTRACT' : selectedType}
                              />
                            </Grid>
                          )}
                          {jobFieldSettings.compensation?.hide === false && (
                            <>
                              <Grid item sm={11.5}>
                                <Stack direction="row" spacing={4}>
                                  <Grid item sm={3}>
                                    <WsSelect
                                      name="salaryCurrency"
                                      label="Select Currency"
                                      selectArray={currencies}
                                      variant="outlined"
                                      required={jobFieldSettings.compensation?.mandatory}
                                      showMandatorySymbol={jobFieldSettings.compensation?.mandatory}
                                      onChangeValue={(val) =>
                                        methods.setValue('clientBillRateCurrency', val)
                                      }
                                    />
                                  </Grid>
                                  <Grid item sm={3}>
                                    <WsInputNumber
                                      name="minimumSalary"
                                      label="Min Salary"
                                      variant="outlined"
                                      required={jobFieldSettings.compensation?.mandatory}
                                      showMandatorySymbol={jobFieldSettings.compensation?.mandatory}
                                    />
                                    <FormHelperText error>
                                      {errors?.minimumSalary?.message}
                                    </FormHelperText>
                                  </Grid>
                                  <Grid item sm={3}>
                                    <WsInputNumber
                                      name="maximumSalary"
                                      label="Max Salary"
                                      variant="outlined"
                                      required={jobFieldSettings.compensation?.mandatory}
                                      showMandatorySymbol={jobFieldSettings.compensation?.mandatory}
                                    />
                                    <FormHelperText error>
                                      {errors.salaryCurrency &&
                                        errors?.salaryCurrency['maximumSalary']?.message}
                                    </FormHelperText>
                                  </Grid>
                                  <Grid item sm={3}>
                                    <WsSelect
                                      name="salaryTimeSpan"
                                      label="Unit"
                                      selectArray={salaryTimeSpans}
                                      required={jobFieldSettings.compensation?.mandatory}
                                      variant="outlined"
                                      showMandatorySymbol={jobFieldSettings.compensation?.mandatory}
                                      onChangeValue={(val) =>
                                        methods.setValue('clientBillRateTimeSpan', val)
                                      }
                                    />
                                    <FormHelperText error>
                                      {errors.salaryCurrency &&
                                        errors?.salaryCurrency['salaryTimeSpan']?.message}
                                    </FormHelperText>
                                  </Grid>
                                </Stack>
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.clientBillRate?.hide === false && isValidContractType && (
                            <Grid item sm={11.5}>
                              <Label>
                                Client Bill Rate
                                {jobFieldSettings.clientBillRate?.mandatory ? ' *' : ''}
                              </Label>
                              <Stack direction="row" spacing={4} sx={{ mt: 1 }}>
                                <Grid item sm={3}>
                                  <WsSelect
                                    name="clientBillRateCurrency"
                                    placeholder="Currency"
                                    selectArray={currencies}
                                    variant="outlined"
                                    required={jobFieldSettings.clientBillRate?.mandatory}
                                    showMandatorySymbol={jobFieldSettings.clientBillRate?.mandatory}
                                  />
                                </Grid>
                                <Grid item sm={3}>
                                  <WsInputNumber
                                    name="clientBillRate"
                                    placeholder="Bill Rate"
                                    variant="outlined"
                                    required={jobFieldSettings.clientBillRate?.mandatory}
                                    showMandatorySymbol={jobFieldSettings.clientBillRate?.mandatory}
                                  />
                                  <FormHelperText error>
                                    {errors.clientBillRate?.message}
                                  </FormHelperText>
                                </Grid>
                                <Grid item sm={3}>
                                  <WsSelect
                                    name="clientBillRateTimeSpan"
                                    placeholder="Unit"
                                    selectArray={salaryTimeSpans}
                                    variant="outlined"
                                    required={jobFieldSettings.clientBillRate?.mandatory}
                                    showMandatorySymbol={jobFieldSettings.clientBillRate?.mandatory}
                                  />
                                  <FormHelperText error>
                                    {errors.clientBillRateCurrency &&
                                      errors.clientBillRateCurrency['clientBillRateTimeSpan']
                                        ?.message}
                                  </FormHelperText>
                                </Grid>
                                <Grid item sm={3}></Grid>
                              </Stack>
                            </Grid>
                          )}
                          {jobFieldSettings.payRateExpenses?.hide === false && isValidContractType && (
                            <Grid item sm={11.5}>
                              <Label>
                                Pay Rate Expenses
                                {jobFieldSettings.payRateExpenses?.mandatory ? ' *' : ''}
                              </Label>
                              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Grid item sm={3}>
                                  <Controller
                                    control={methods.control}
                                    name="payRateExpensesType"
                                    render={({ field: { onChange, value } }) => (
                                      <WsToggleButton
                                        buttonGroupPadding={6}
                                        buttonsList={payRateExpensesTypes}
                                        defaultValue={value ?? 'FIXED'}
                                        selectedIndexValue={onChange}
                                      />
                                    )}
                                  />
                                </Grid>
                                <Box sx={{ flexGrow: 1 }}>
                                  {payRateExpensesType === 'PERCENTAGE' ? (
                                    <Grid item sm={3.6}>
                                      <Controller
                                        control={methods.control}
                                        name="payRateExpenses"
                                        render={({ field: { value, onChange }, fieldState }) => (
                                          <Autocomplete
                                            value={String(value ?? '')}
                                            freeSolo
                                            size="small"
                                            selectOnFocus
                                            handleHomeEndKeys
                                            options={['5', '10']}
                                            onInputChange={(event, value) => onChange(value)}
                                            renderInput={(params) => (
                                              <TextField
                                                {...params}
                                                required={
                                                  jobFieldSettings.payRateExpenses?.mandatory
                                                }
                                                label="Pay Rate Expenses"
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                              />
                                            )}
                                          />
                                        )}
                                      />
                                    </Grid>
                                  ) : (
                                    <Stack direction="row" spacing={4}>
                                      <Grid item sm={3.6}>
                                        <WsSelect
                                          placeholder="Currency"
                                          name="payRateExpensesCurrency"
                                          required={jobFieldSettings.payRateExpenses?.mandatory}
                                          showMandatorySymbol={
                                            jobFieldSettings.payRateExpenses?.mandatory
                                          }
                                          selectArray={currencies}
                                          size="small"
                                        />
                                      </Grid>
                                      <Grid item sm={3.6}>
                                        <ControlledTextInput
                                          fullWidth
                                          name="payRateExpenses"
                                          required={jobFieldSettings.payRateExpenses?.mandatory}
                                          showMandatorySymbol={
                                            jobFieldSettings.payRateExpenses?.mandatory
                                          }
                                          placeholder="Pay Rate Expenses"
                                          size="small"
                                          inputProps={{}}
                                        />
                                      </Grid>
                                    </Stack>
                                  )}
                                </Box>
                              </Stack>
                            </Grid>
                          )}
                          {jobFieldSettings.skills?.hide === false && (
                            <>
                              <Grid item sm={11.5}>
                                <WsAutoComplete
                                  onValueEnter={onSkillInputKey}
                                  onTagsChange={onSkillSelection}
                                  placeholder="Type to Select Skills"
                                  initialValue={jobData?.skills ?? []}
                                  optionsInStringArray={skillList}
                                  multiple={true}
                                  freeSolo={true}
                                  name="skills"
                                  label="Skills"
                                  variant="outlined"
                                  size="small"
                                  showMandatorySymbol={jobFieldSettings.skills?.mandatory}
                                />
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.description?.hide === false && (
                            <>
                              <Grid item sm={11.5}>
                                <Label>
                                  Job Description
                                  {jobFieldSettings.description?.mandatory ? ' *' : ''}
                                </Label>
                                <Box minHeight={200} border={1} borderColor={'gray'}>
                                  <WsEditor editor={jdEditor} resizable={true}></WsEditor>
                                </Box>
                                <FormHelperText error>{errors.description?.message}</FormHelperText>
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.jobStatusType?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <WsButtonGroup
                                  inputTitle="Job Status"
                                  buttonsList={jobStatusList}
                                  selectedIndexValue={onJobStatusChange}
                                  showMandatorySymbol={jobFieldSettings.jobStatusType?.mandatory}
                                  defaultValue={jobStatusDefaultValueIndex()}
                                  size="large"
                                />
                                <FormHelperText error>
                                  {errors.jobStatusType?.message}
                                </FormHelperText>
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.jobIndustryName?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <WsSelect
                                  name="jobIndustryName"
                                  label="Select Industry"
                                  selectArray={jobIndustryList}
                                  variant="outlined"
                                  showMandatorySymbol={jobFieldSettings.jobIndustryName?.mandatory}
                                ></WsSelect>
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.jobCategory?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <WsSelect
                                  name="jobCategory"
                                  label="Select Category"
                                  selectArray={selectedCategory}
                                  variant="outlined"
                                  showMandatorySymbol={jobFieldSettings.jobCategory?.mandatory}
                                ></WsSelect>
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.jobOccupation?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <WsSelect
                                  name="jobOccupation"
                                  label="Job Occupation"
                                  selectArray={jobOccupations}
                                  variant="outlined"
                                  showMandatorySymbol={jobFieldSettings.jobOccupation?.mandatory}
                                ></WsSelect>
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.tags?.hide === false && (
                            <>
                              <Grid item sm={11.5}>
                                <WsAutoComplete
                                  onValueEnter={onTagInputKey}
                                  onTagsChange={onJobTagSelection}
                                  placeholder="Enter Tags, comma seprated"
                                  initialValue={jobData?.tags ?? []}
                                  optionsInStringArray={tags}
                                  multiple={true}
                                  freeSolo={true}
                                  name="tags"
                                  label="Tags"
                                  size="small"
                                  variant="outlined"
                                  showMandatorySymbol={jobFieldSettings.tags?.mandatory}
                                />
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.insight?.hide === false && (
                            <>
                              <Grid item sm={11.5}>
                                <Label>
                                  Job Insights {jobFieldSettings.insight?.mandatory ? ' *' : ''}
                                </Label>
                                <Box minHeight={200} border={1} borderColor={'gray'}>
                                  <WsEditor editor={jobInsightEditor}></WsEditor>
                                </Box>
                                <FormHelperText error>
                                  {getFieldState('insights')?.error?.message}
                                </FormHelperText>
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.workAuthorization?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <ControlledTextInput
                                  name="workAuthorization"
                                  labelText="Work Authorization"
                                  type="text"
                                  fullWidth
                                  placeholder="Eg, Work Authorization"
                                  variant="outlined"
                                  autoComplete="workAuthorization"
                                  showMandatorySymbol={
                                    jobFieldSettings.workAuthorization?.mandatory
                                  }
                                />
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.url?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <ControlledTextInput
                                  name="url"
                                  labelText="Job URL"
                                  type="text"
                                  fullWidth
                                  placeholder="Job URL"
                                  variant="outlined"
                                  autoComplete="url"
                                  showMandatorySymbol={jobFieldSettings.url?.mandatory}
                                />
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.otherBenefitDetails?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <ControlledTextInput
                                  name="otherBenefitDetails"
                                  labelText="Benefits"
                                  type="text"
                                  fullWidth
                                  placeholder="Benefits"
                                  variant="outlined"
                                  autoComplete="otherBenefitDetails"
                                  showMandatorySymbol={
                                    jobFieldSettings.otherBenefitDetails?.mandatory
                                  }
                                />
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.jobSource?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <ControlledTextInput
                                  name="jobSource"
                                  labelText="Job Source"
                                  type="text"
                                  fullWidth
                                  placeholder="Job Source"
                                  variant="outlined"
                                  autoComplete="jobSource"
                                  showMandatorySymbol={jobFieldSettings.jobSource?.mandatory}
                                />
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.visaRequirements?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <ControlledTextInput
                                  name="visaRequirements"
                                  labelText="Visa Requirements"
                                  type="text"
                                  fullWidth
                                  placeholder="Visa Requirements"
                                  variant="outlined"
                                  autoComplete="visaRequirements"
                                  showMandatorySymbol={jobFieldSettings.visaRequirements?.mandatory}
                                  size="small"
                                />
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.hiringFor?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <WsSelect
                                  name="hiringFor"
                                  label="Hiring For"
                                  selectArray={hiringForList}
                                  variant="outlined"
                                  size="small"
                                  showMandatorySymbol={jobFieldSettings.hiringFor?.mandatory}
                                ></WsSelect>
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.experienceRequirements?.hide === false && (
                            <>
                              <Grid item sm={5.75}>
                                <ControlledTextInput
                                  name="experienceRequirements"
                                  labelText="Years Of Experience"
                                  type="text"
                                  fullWidth
                                  placeholder="Ex: 5-10 Years"
                                  variant="outlined"
                                  autoComplete="experienceRequirements"
                                  showMandatorySymbol={
                                    jobFieldSettings.experienceRequirements?.mandatory
                                  }
                                  size="small"
                                />
                              </Grid>
                            </>
                          )}
                          {jobFieldSettings.educationRequirements?.hide === false && (
                            <Grid item sm={5.75}>
                              <ControlledTextInput
                                name="educationRequirements"
                                labelText="Education"
                                type="text"
                                fullWidth
                                placeholder="Add Education"
                                variant="outlined"
                                autoComplete="educationRequirements"
                                showMandatorySymbol={
                                  jobFieldSettings.educationRequirements?.mandatory
                                }
                                size="small"
                              />
                            </Grid>
                          )}
                          {jobFieldSettings.workDuration?.hide === false && (
                            <>
                              <Grid item sm={2.87}>
                                <WsInputNumber
                                  name="workDuration"
                                  label="Work Duraton"
                                  variant="outlined"
                                  minValue={1}
                                  maxValue={80}
                                  showMandatorySymbol={jobFieldSettings.workDuration?.mandatory}
                                ></WsInputNumber>
                              </Grid>
                              <Grid item sm={2.88}>
                                <Label>&nbsp;</Label>
                                <WsSelect
                                  name="workDurationTimeSpan"
                                  label=""
                                  selectArray={workDurationTimpSpans}
                                  required={jobFieldSettings.workDuration?.mandatory}
                                  variant="outlined"
                                  showMandatorySymbol={jobFieldSettings.workDuration?.mandatory}
                                ></WsSelect>
                              </Grid>
                            </>
                          )}
                        </Grid>
                        {
                          <Grid item sm={5.75}>
                            <WsCheckBox name="useRedactedCv" label="Use Redacted CV" bold={false} />
                          </Grid>
                        }
                        {jobFieldSettings.submissionFormTemplete?.hide === false && (
                          <Grid item sm={5.75}>
                            <SubmissionFormTemplateAutoComplete
                              name="submissionFormTemplete"
                              placeholder="Select Template"
                              showMandatorySymbol={
                                jobFieldSettings.submissionFormTemplete.mandatory
                              }
                            />
                          </Grid>
                        )}
                        <Grid item sm={4}>
                          {isQuestionGeneretedAndSaved ? (
                            <>
                              <Label>Interview questions</Label>

                              <Tooltip title="View Genereted Question." placement="top">
                                <Box textAlign="left" display="flex" alignItems="center">
                                  <img
                                    src={ArtificialIntelligence}
                                    alt="ArtificialIntelligence Logo"
                                    style={{
                                      width: 30,
                                      height: 30,
                                      marginRight: 8,
                                    }}
                                  />
                                  <WsButton
                                    type="button"
                                    onClick={() => setOpenQuestionsDialog(true)}
                                  >
                                    {`View Generated Questions (${jobQuestions.length})`}
                                  </WsButton>
                                </Box>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Label>Interview questions</Label>

                              <Tooltip
                                title="For best results please update JD to include minimum of 200 characters."
                                placement="top"
                              >
                                <Box textAlign="left" display="flex" alignItems="center">
                                  <img
                                    src={ArtificialIntelligence}
                                    alt="ArtificialIntelligence Logo"
                                    style={{
                                      width: 30,
                                      height: 30,
                                      marginRight: 8,
                                    }}
                                  />
                                  <WsButton type="button" onClick={onGenerateQuestions}>
                                    {getQuestionButtonText()}
                                  </WsButton>
                                </Box>
                              </Tooltip>
                            </>
                          )}
                        </Grid>

                        <Grid item sm={4}>
                          <Label>Add Evaluation</Label>
                          <WsButton
                            type="button"
                            variant="outlined"
                            onClick={() => setOpenEvaluationDrawer(true)}
                          >
                            + Select Evaluation Template
                          </WsButton>
                        </Grid>
                        <Grid item sm={4}>
                          <WsTagsList
                            chipArray={
                              selectedEvaluations.map(
                                (evaluations: IEValuationTemplateDetails) =>
                                  evaluations.templateName
                              ) ?? []
                            }
                            count={8}
                            type={ChipsType.PRIMARY}
                            allowDelete={true}
                            onDelete={(item) => onEvaluationTagDelete(item)}
                          />
                        </Grid>
                        <Grid item sm={4}>
                          <Label>Add Information Template</Label>
                          <WsButton
                            type="button"
                            variant="outlined"
                            onClick={() => setOpenInformationDrawer(true)}
                          >
                            + Select Information Template
                          </WsButton>
                        </Grid>
                        <Grid item sm={4}>
                          <WsTagsList
                            chipArray={
                              selectedInfoTemplates.map(
                                (templates: IInformationTemplateDetails) => templates.categoryName
                              ) ?? []
                            }
                            count={8}
                            type={ChipsType.PRIMARY}
                            allowDelete={true}
                            onDelete={(item) => onInfoTagDelete(item)}
                          />
                        </Grid>

                        <Grid item sm={12}>
                          <FormControlLabel
                            name="addToCareerPage"
                            control={
                              <Checkbox
                                checked={isCareerPage}
                                color="primary"
                                onChange={(e) => handleCheckbox(e)}
                              />
                            }
                            label="Post on Wisestep jobs pages and receive additional candidate applications"
                          />
                        </Grid>
                        <br />
                        {/* <JobFormBottomButtons
                        leftLinkText="Save & Exit"
                        leftButtonText="Publish"
                        leftButtonIcon="/images/Publish.png"
                        rightButtonText="Continue"
                        rightButtonIcon="/images/Vector.png"
                        buttonSize="medium"
                      /> */}
                        <Grid item sm={11} textAlign="right">
                          <Button
                            type="submit"
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={disableSubmitButton}
                            endIcon={
                              disableSubmitButton ? (
                                <CircularProgress size={17} color="secondary" />
                              ) : undefined
                            }
                          >
                            {disableSubmitButton ? 'Saving...' : 'Continue'}
                          </Button>
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                </Stack>
              </Paper>
            </Box>
          </form>
        </FormProvider>
      </FormWizard>
      {openNewClientDialogue && (
        <CreateNewClientDialog
          open={openNewClientDialogue}
          clientNameInputVal={companyNameNewVal}
          onClose={setOpenNewClientDialogue}
          onSelect={onClientCreateFromDialogue}
          isMarkInactive={isMarkInactive}
        />
      )}
      {openClientContactDialog && companyName && (
        <CreateNewClientContactDialog
          open={openClientContactDialog}
          clientId={methods.getValues('clientId') ?? jobData.clientId ?? ''}
          clientName={companyName ?? ''}
          clientRecruiterEmailInputVal={companyContactInput ?? ''}
          onClose={() => setOpenClientContactDialog(false)}
          onSelect={onClientContactCreateFromDialog}
        />
      )}
      {openFeeArrangementDialog && companyName && (
        <CreateFeeArrangementDialog
          open={openFeeArrangementDialog}
          onClose={() => setOpenFeeArrangementDialog(false)}
          clientId={methods?.getValues('clientId') ?? ''}
          companyName={companyName}
          onSave={() => {
            setOpenFeeArrangementDialog(false);
            setIsFeeArrangementAdded(!isFeeArrangementAdded);
          }}
        />
      )}
      {openEvaluationDrawer && (
        <SelectEvaluationDrawer
          onClose={() => setOpenEvaluationDrawer(false)}
          open={openEvaluationDrawer}
          setSelectedEvaluations={setSelectedEvaluations}
          selectedTemplates={selectedEvaluations}
        />
      )}
      {openInformationDrawer && (
        <SelectInformationTemplateDrawer
          onClose={() => setOpenInformationDrawer(false)}
          open={openInformationDrawer}
          setSelectedTemplates={(infoTemplates, hasRtrTemplates) => {
            setSelectedInfoTemplates(infoTemplates);
            setHasRtrTemplates(hasRtrTemplates);
          }}
          selectedTemplates={selectedInfoTemplates}
        />
      )}
      {openQuestionsDialog && (
        <WsDialog openDialog={openQuestionsDialog} size={WsDialogSize.EXTRALARGE}>
          <DialogTitle
            sx={{
              backgroundColor: '#f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" align="left">
              Interview Question
            </Typography>
          </DialogTitle>
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" align="left">
              {jobDataNew.title != undefined
                ? `Interview Questions For The Role Of ${jobDataNew.title}(${jobDataNew.jobCode})`
                : `Interview Questions For The Current Role`}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container p={6} spacing={5} justifyContent="center">
              {jobQuestions.map((question, index) => (
                <ListItem key={index}>
                  <ListItemText primary={`${index + 1}. ${question}`} />
                  <Grid sx={{ marginRight: '-45px' }}>
                    <WsElementWithPopOver element={<WsIconMoreVer />}>
                      <Box p={1}>
                        <WsTypography
                          variant={WsTypoVariants.SUB_TITLE}
                          cursor="pointer"
                          onClick={() => onEditQuestion(question)}
                          color={WsTypoColors.TERTIARY900}
                        >
                          <BorderColorOutlinedIcon
                            style={{
                              marginRight: '8px',
                              verticalAlign: 'bottom',
                              color: '#484D6D',
                            }}
                          />
                          Edit Question
                        </WsTypography>
                      </Box>
                      <Box p={1}>
                        <WsTypography
                          variant={WsTypoVariants.SUB_TITLE}
                          cursor="pointer"
                          onClick={() => onDeleteQuestion(question)}
                          color={WsTypoColors.TERTIARY900}
                        >
                          <DeleteOutlineIcon
                            style={{
                              marginRight: '8px',
                              verticalAlign: 'bottom',
                              color: '#484D6D',
                            }}
                          />
                          Delete Question
                        </WsTypography>
                      </Box>
                    </WsElementWithPopOver>
                  </Grid>
                </ListItem>
              ))}
            </Grid>
          </DialogContent>
          <DialogContent style={{ overflow: 'hidden', justifyContent: 'flex-start' }}>
            <Divider />
          </DialogContent>
          <DialogActions style={{ justifyContent: 'flex-start' }}>
            {openQuestionEditDialog && (
              <Grid container p={3} spacing={1} justifyContent="center" sx={{ marginTop: '-50px' }}>
                <Grid item xs={12}>
                  <Typography variant="h6" align="left">
                    Edit question
                  </Typography>
                </Grid>
                {warningStatus ? (
                  <Grid item xs={12} sx={{ marginBottom: '-25px' }}>
                    <TextField
                      size={'small'}
                      sx={{
                        width: '80%',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'red',
                          },
                          '&:hover fieldset': {
                            borderColor: 'red',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'red',
                          },
                        },
                      }}
                      value={textFieldValue}
                      onChange={handleInputChange}
                      error={true}
                      helperText="Edit this question before taking another action."
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <TextField
                      size={'small'}
                      sx={{ width: '80%' }}
                      value={textFieldValue}
                      onChange={handleInputChange}
                    />
                  </Grid>
                )}

                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                  sx={{ marginTop: '-50px', marginRight: '72px' }}
                >
                  <WsButton
                    size="medium"
                    variant="contained"
                    onClick={() => {
                      editQuestion();
                      setOpenQuestionEditDialog(false);
                      setWarningStatus(false);
                    }}
                  >
                    Update
                  </WsButton>
                  <WsButton
                    size="medium"
                    variant="outlined"
                    onClick={() => {
                      setOpenQuestionEditDialog(false);
                      setWarningStatus(false);
                    }}
                  >
                    Cancel
                  </WsButton>
                </Grid>
              </Grid>
            )}
          </DialogActions>
          <DialogActions style={{ justifyContent: 'flex-start' }} sx={{ marginBottom: '-30px' }}>
            {!openAddQuestionDialog && (
              <Grid
                item
                xs={12}
                display="flex"
                justifyContent="flex-start"
                gap={2}
                sx={{ marginBottom: '-30px', marginLeft: '30px' }}
              >
                <WsButton
                  type="button"
                  size="large"
                  variant="text"
                  onClick={() => {
                    openQuestionEditDialog
                      ? setWarningStatus(true)
                      : setOpenAddQuestionDialog(true);
                  }}
                >
                  + Add question manually
                </WsButton>
              </Grid>
            )}

            {openAddQuestionDialog && (
              <Grid
                container
                p={3}
                spacing={1}
                justifyContent="center"
                sx={{ marginTop: '-50px', marginBottom: '10px' }}
              >
                <Grid item xs={12}>
                  <Typography variant="h6" align="left">
                    Add question manually
                  </Typography>
                </Grid>
                {warningStatus ? (
                  <Grid item xs={12} sx={{ marginBottom: '-25px' }}>
                    <TextField
                      size="small"
                      sx={{
                        width: '80%',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'red',
                          },
                          '&:hover fieldset': {
                            borderColor: 'red',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'red',
                          },
                        },
                      }}
                      value={textFieldValueOnAdd}
                      placeholder="Type your question here"
                      onChange={handleInputChangeOnAdd}
                      error={true}
                      helperText="Add this question before taking another action."
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      sx={{
                        width: '80%',
                      }}
                      value={textFieldValueOnAdd}
                      placeholder="Type your question here"
                      onChange={handleInputChangeOnAdd}
                    />
                  </Grid>
                )}

                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                  sx={{ marginTop: '-50px', marginRight: '72px' }}
                >
                  <WsButton
                    size="medium"
                    variant="contained"
                    onClick={() => {
                      addQuestion(textFieldValueOnAdd);
                      setOpenAddQuestionDialog(false);
                      setWarningStatus(false);
                    }}
                  >
                    Add
                  </WsButton>
                  <WsButton
                    size="medium"
                    variant="outlined"
                    onClick={() => {
                      setWarningStatus(false);
                      setOpenAddQuestionDialog(false);
                    }}
                  >
                    Cancel
                  </WsButton>
                </Grid>
              </Grid>
            )}
          </DialogActions>
          <DialogActions sx={{ marginBottom: '30px' }}>
            {isQuestionGeneretedAndSaved ? (
              <Box textAlign="left" sx={{ marginRight: '20px' }}>
                <WsButton
                  size="large"
                  variant="outlined"
                  onClick={() => {
                    setOpenQuestionsDialog(false);
                    setOpenAddQuestionDialog(false);
                    setOpenQuestionEditDialog(false);
                    onCancel(jobQuestionsTemp);
                  }}
                  disabled={openAddQuestionDialog || openQuestionEditDialog}
                >
                  Cancel
                </WsButton>
              </Box>
            ) : (
              <Box textAlign="left" sx={{ marginRight: '20px' }}>
                <WsButton
                  size="large"
                  variant="outlined"
                  onClick={() => {
                    setJobQuestions([]);
                    setJobQuestionsTemp([]);
                    setOpenQuestionsDialog(false);
                    setOpenAddQuestionDialog(false);
                    setOpenQuestionEditDialog(false);
                  }}
                  disabled={openAddQuestionDialog || openQuestionEditDialog}
                >
                  Discard
                </WsButton>
              </Box>
            )}
            <CustomTooltip
              title={
                openAddQuestionDialog || openQuestionEditDialog
                  ? 'Complete above actions to enable Continue.'
                  : ''
              }
              placement="top"
              arrow
            >
              <Box textAlign="left" sx={{ marginRight: '40px' }}>
                <WsButton
                  size="large"
                  variant="contained"
                  onClick={onSaveQuestionResponse}
                  disabled={openAddQuestionDialog || openQuestionEditDialog}
                >
                  Save
                </WsButton>
              </Box>
            </CustomTooltip>
          </DialogActions>
        </WsDialog>
      )}
    </>
  );
};

export default JobPostingForm;
